import type { WebRTCApiProvider } from "@signalwire/js";

/**
 * SignalWire's inbound WebRTC path requests getUserMedia with audio plus `video: {}`
 * (merged empty constraints). An empty object is truthy, so the browser asks for
 * camera; if that request fails, the whole call fails and there is no mic audio.
 *
 * Ring-group PSTN answering is voice-only. When both audio and video are requested,
 * force `video: false`. Single-kind requests (e.g. video-only device checks from
 * enumerateDevices flows) are left unchanged.
 */
export function createVoiceOnlyInboundWebRTCApiProvider(): WebRTCApiProvider {
  const md = navigator.mediaDevices;
  return {
    RTCPeerConnection,
    mediaDevices: {
      getUserMedia(constraints: MediaStreamConstraints) {
        const c = constraints as MediaStreamConstraints & {
          audio?: unknown;
          video?: unknown;
        };
        const audioRequested = c.audio !== false && c.audio !== undefined;
        const videoRequested = c.video !== false && c.video !== undefined;
        if (audioRequested && videoRequested) {
          return md.getUserMedia({ ...c, video: false });
        }
        return md.getUserMedia(constraints);
      },
      enumerateDevices: () => md.enumerateDevices(),
      addEventListener: (
        ...args: Parameters<MediaDevices["addEventListener"]>
      ) => {
        md.addEventListener(...args);
      },
      removeEventListener: (
        ...args: Parameters<MediaDevices["removeEventListener"]>
      ) => {
        md.removeEventListener(...args);
      },
    },
  };
}
